/* eslint-disable import/no-cycle */
import { AsyncAction, Action } from 'types/state';

export enum GameStatus {
  // Game state
  NOT_STARTED = 'not_started',
  IN_PROGRESS = 'in_progress',
  ABORTED = 'aborted',

  // Basic outcomes
  BLACK_WIN = 'black_win',
  WHITE_WIN = 'white_win',
  DRAW = 'draw',

  // Specific win reasons
  BLACK_WIN_CHECKMATE = 'black_win_checkmate',
  WHITE_WIN_CHECKMATE = 'white_win_checkmate',
  BLACK_WIN_RESIGNATION = 'black_win_resignation',
  WHITE_WIN_RESIGNATION = 'white_win_resignation',
  BLACK_WIN_TIME = 'black_win_time',
  WHITE_WIN_TIME = 'white_win_time',
  BLACK_WIN_ABANDONMENT = 'black_win_abandonment',
  WHITE_WIN_ABANDONMENT = 'white_win_abandonment',

  // Specific draw reasons
  DRAW_AGREEMENT = 'draw_agreement',
  DRAW_STALEMATE = 'draw_stalemate',
  DRAW_REPETITION = 'draw_repetition',
  DRAW_FIFTY_MOVE = 'draw_fifty_move',
  DRAW_INSUFFICIENT_MATERIAL = 'draw_insufficient_material',
}

export enum GameSource {
  STATIC = 'static',
  LOOP = 'loop',
  USER = 'user',
  STREAMER = 'streamer',
}

export interface Player {
  name: string
  elo: number
}

export interface GameOdds {
  white_win: number
  draw: number
  black_win: number
}

export interface AnonMoveWager {
  data: string
  amount: number
  is_bot?: boolean
}

export interface PoolWagerState {
  options: string[]
  wagers: AnonMoveWager[]
}

export interface Move {
  san: string
  to: string
  from: string
  time: number
  is_white: boolean
}

export interface GameChat {
  gameId: string
  userId: string
  userName: string
  chat: string
  time: string
}

export interface FeedChat extends GameChat {
  type: 'message'
}

export interface Game {
  complete: boolean
  game_status: string
  time_format: string
  source: GameSource
  move_hist: Move[]
  player_black: Player
  player_white: Player
  state: string
  time_black: number
  time_white: number
  odds: GameOdds
  pool_wagers: {
    move: PoolWagerState
  },
  // Optional server-provided badge metadata (opening/emoji/dominance)
  badge_meta?: any
  _id: string
  created_at: string
  updated_at: string
}

/* -------- State -------- */

export interface PendingBet {
  moveString: string;
  stake: number;
  gameId: string;
  isActive: boolean;
}

export interface GameStats {
  gameId: string;
  viewerCount: number;
  moveWagerData: {
    [moveNumber: string]: {
      totalAmount: number;
      betCount: number;
    };
  };
  wdlWagerTotals: {
    [outcome: string]: {
      totalAmount: number;
      betCount: number;
      averageOdds: number;
    };
  };
  currentMoveNumber: number;
  gameStatus: string;
}

export interface GameState {
  games: Record<string, Game>,
  showModal: Record<string, boolean>,
  chats: FeedChat[],
  quickBetMode: boolean;
  pendingBet: PendingBet | null;
  gameStats: Record<string, GameStats>;
}

/* -------- Action Types -------- */

export const JOIN_GAME = 'JOIN_GAME'; // ws
export const LEAVE_GAME = 'LEAVE_GAME'; // ws
export const START_GAME = 'START_GAME'; // ws
export const UPDATE_GAME_STATE = 'UPDATE_GAME_STATE'; // ws
export const UPDATE_GAME_ODDS = 'UPDATE_GAME_ODDS'; // ws
export const UPDATE_GAME_END = 'UPDATE_GAME_END'; // ws
export const BROADCAST_POOL_WAGER = 'BROADCAST_POOL_WAGER'; // ws
export const GAME_CHAT = 'GAME_CHAT'; // ws

export const FETCH_GAME = 'FETCH_GAME';
export const FETCH_GAMES = 'FETCH_GAMES';
export const UPDATE_SHOW_MODAL = 'UPDATE_SHOW_MODAL';
export const CLEAR_GAMES = 'CLEAR_GAMES';
export const TOGGLE_QUICK_BET = 'TOGGLE_QUICK_BET';
export const SET_PENDING_BET = 'SET_PENDING_BET';
export const CLEAR_PENDING_BET = 'CLEAR_PENDING_BET';
export const FETCH_GAME_STATS = 'FETCH_GAME_STATS';
export const UPDATE_VIEWER_COUNT = 'UPDATE_VIEWER_COUNT';

export type JoinGameData = { gameId: string }; // ws
export type LeaveGameData = { gameId: string }; // ws
export type StartGameData = { gameId: string, game_status: GameStatus }; // ws
export type UpdateGameStateData = { gameId: string, state: string, move_hist: Move[], time_white: number, time_black: number, pool_wagers: { move: PoolWagerState } }; // ws
export type UpdateGameOddsData = { gameId: string, odds: GameOdds, pool_wagers: { move: PoolWagerState }, badge_meta?: any }; // ws
export type UpdateGameEndData = { gameId: string, complete: boolean, game_status: string }; // ws
export type BroadcastPoolWager = { gameId: string, type: 'move', data: string, amount: number }; // ws
export type GameChatMessage = GameChat;

export type FetchGameRequestData = { id: string };
export type FetchGamesRequestData = { game_status: string[] };
export type FetchGameStatsRequestData = { id: string };

export type FetchGameData = Game;
export type FetchGamesData = Game[];
export type FetchGameStatsData = GameStats;

export type UpdateModalData = { gameId: string, modalState: boolean };
export type SetPendingBetData = PendingBet;
export type UpdateViewerCountData = { gameId: string, viewerCount: number };

export type JoinGameActions = AsyncAction<typeof JOIN_GAME, JoinGameData, JoinGameData>; // ws
export type LeaveGameActions = AsyncAction<typeof LEAVE_GAME, LeaveGameData, LeaveGameData>; // ws
export type StartGameActions = AsyncAction<typeof START_GAME, StartGameData>; // ws
export type UpdateGameStateActions = AsyncAction<typeof UPDATE_GAME_STATE, UpdateGameStateData>; // ws
export type UpdateGameOddsActions = AsyncAction<typeof UPDATE_GAME_ODDS, UpdateGameOddsData>; // ws
export type UpdateGameEndActions = AsyncAction<typeof UPDATE_GAME_END, UpdateGameEndData>; // ws
export type BroadcastPoolWagerActions = AsyncAction<typeof BROADCAST_POOL_WAGER, BroadcastPoolWager>; // ws
export type GameChatActions = AsyncAction<typeof GAME_CHAT, GameChatMessage, Omit<GameChatMessage, 'userId' | 'userName'>>;

export type FetchGameActions = AsyncAction<typeof FETCH_GAME, FetchGameData, FetchGameRequestData>;
export type FetchGamesActions = AsyncAction<typeof FETCH_GAMES, FetchGamesData, FetchGamesRequestData>;
export type FetchGameStatsActions = AsyncAction<typeof FETCH_GAME_STATS, FetchGameStatsData, FetchGameStatsRequestData>;
export type ShowModalActions = Action<typeof UPDATE_SHOW_MODAL, UpdateModalData>;
export type ClearGamesActions = Action<typeof CLEAR_GAMES>;
export type ToggleQuickBetActions = Action<typeof TOGGLE_QUICK_BET>;
export type SetPendingBetActions = Action<typeof SET_PENDING_BET, SetPendingBetData>;
export type ClearPendingBetActions = Action<typeof CLEAR_PENDING_BET>;
export type UpdateViewerCountActions = Action<typeof UPDATE_VIEWER_COUNT, UpdateViewerCountData>;

export type GameUpdateActions = StartGameActions | UpdateGameStateActions | UpdateGameOddsActions | UpdateGameEndActions;

export type GameActions =
  JoinGameActions | LeaveGameActions | GameUpdateActions | ShowModalActions | ClearGamesActions |
  FetchGameActions | FetchGamesActions | FetchGameStatsActions | BroadcastPoolWagerActions | GameChatActions |
  ToggleQuickBetActions | SetPendingBetActions | ClearPendingBetActions | UpdateViewerCountActions;

export type GameActionTypes =
  typeof JOIN_GAME | typeof LEAVE_GAME | typeof UPDATE_GAME_STATE | typeof UPDATE_GAME_ODDS | typeof UPDATE_GAME_END | typeof FETCH_GAME |
  typeof FETCH_GAME | typeof START_GAME | typeof FETCH_GAMES | typeof FETCH_GAME_STATS | typeof BROADCAST_POOL_WAGER | typeof UPDATE_SHOW_MODAL | typeof CLEAR_GAMES |
  typeof GAME_CHAT | typeof TOGGLE_QUICK_BET | typeof SET_PENDING_BET | typeof CLEAR_PENDING_BET | typeof UPDATE_VIEWER_COUNT;
